import React, { useEffect, useState } from 'react';
import { listAdminUsers, createAdminUser, deleteAdminUser } from '../lib/adminApi';
import { useAuth } from '../context/AuthContext';

const EMPTY = { username: '', password: '', display_name: '' };

export default function SystemAccess() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user: currentUser } = useAuth();

  const load = () => {
    setLoading(true);
    listAdminUsers()
      .then((data) => setUsers(data.users || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await createAdminUser(form);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username) => {
    setError('');
    try {
      await deleteAdminUser(username);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>System Access</h1>
          <p>Create or remove administrator accounts for this dashboard.</p>
        </div>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-card">
        <h2>Add Administrator</h2>
        <p className="admin-card-subtitle">New accounts can sign in immediately with the password set here.</p>
        <form className="admin-form" onSubmit={handleCreate}>
          <div className="admin-form-row">
            <div className="admin-field">
              <label>Username *</label>
              <input
                required
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="e.g. sam_l"
              />
            </div>
            <div className="admin-field">
              <label>Display Name</label>
              <input
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              />
            </div>
          </div>
          <div className="admin-field">
            <label>Password *</label>
            <input
              type="password"
              required
              minLength={4}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Adding…' : 'Add Administrator'}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Display Name</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username}>
                <td>{u.username}</td>
                <td>{u.display_name || '—'}</td>
                <td>{new Date(u.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    disabled={u.username === currentUser?.username || users.length <= 1}
                    onClick={() => handleDelete(u.username)}
                    title={u.username === currentUser?.username ? "You can't remove your own account" : 'Remove'}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && users.length === 0 && <div className="admin-empty">No administrators found.</div>}
      </div>
    </div>
  );
}
